"use client";
import { usePowerSearch } from "../contexts/power-search-context";
import MultiSelectWithSearch from "../MultiSelectWithSearch";
import Button from "react-bootstrap/Button";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import {
  POWER_SEARCH_EVENT_NAMES,
  MIXPANEL_PROPS,
} from "shared/trackEvents/constants";

const MultiSelectSectorWithSelectAll = ({
  classificationOptionsList,
  filterKey,
  title,
  allClassificationsType,
  currentFilterValue,
  all_classifications,
  updateFilter,
  updateAllClassificationsToggle,
}) => {
  const currentUser = useSelector((state) => state.users.user);

  if (!classificationOptionsList || classificationOptionsList.length === 0)
    return null;

  const options = classificationOptionsList.map((opt) =>
    typeof opt === "string"
      ? { label: opt, value: opt }
      : { label: opt.name, value: opt.name }
  );

  const handleSelectAll = useCallback(() => {
    const currentAllState = all_classifications[allClassificationsType];
    updateAllClassificationsToggle(allClassificationsType, !currentAllState);
    updateFilter(filterKey, !currentAllState ? options : []);
  }, [
    allClassificationsType,
    all_classifications,
    updateAllClassificationsToggle,
    updateFilter,
    filterKey,
    options,
  ]);

  const handleChange = useCallback(
    (value) => {
      updateFilter(filterKey, value);
      if (value.length === 0 && all_classifications[allClassificationsType]) {
        updateAllClassificationsToggle(allClassificationsType, false);
      }

      // Track sector filter selection
      if (value.length > 0) {
        const eventName = isOpenRemunerationPlan(currentUser)
          ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_SECTOR_CLICKED
          : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_SECTOR_CLICKED;

        mixpanelTrack(eventName, {
          [MIXPANEL_PROPS.FILTER_OPTION]: value[value.length - 1],
          [MIXPANEL_PROPS.FILTER_CATEGORY]: title,
          userEmail: currentUser?.email,
        });
      }
    },
    [
      updateFilter,
      filterKey,
      all_classifications,
      allClassificationsType,
      updateAllClassificationsToggle,
      currentUser,
      title,
    ]
  );

  return (
    <div
      className="mt-2"
      style={{ marginBottom: "10px", paddingBottom: "16px" }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <p
          className="mb-0"
          style={{ fontSize: "14px", fontWeight: "500", color: "#AF6A3A" }}
        >
          {title}
        </p>{" "}
        <Button variant="link" onClick={handleSelectAll} className="small p-0">
          {!all_classifications[allClassificationsType]
            ? "Select all"
            : "Unselect all"}
        </Button>
      </div>
      <MultiSelectWithSearch
        options={options}
        value={currentFilterValue}
        onChange={handleChange}
        placeholder={`Select ${title}`}
        className={"form-input debounce-search-input mt-1"}
      />
    </div>
  );
};

const SectorFilter = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    filterOptions,
    sourceTypes,
    industry_classifications,
    private_classifications,
    superannuation_classifications,
    government_classifications,
    other_government_classifications,
    nfp_classifications,
    all_classifications,
    updateFilter,
    updateAllClassificationsToggle,
  } = usePowerSearch();

  if (sourceTypes.length === 0) return null;

  return (
    <div className="">
      <div
        className="d-flex align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <Button
          variant="link"
          size="sm"
          style={{
            display: "inline-block",
            padding: 0,
            margin: 0,
            textDecoration: "none",
            marginRight: "4px",
            focus: "none",
            outline: "none",
            border: "none",
            boxShadow: "none",
            fontWeight: "bold",
          }}
        >
          {isExpanded ? "−" : "+"}
        </Button>
        <p
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            margin: 0,
          }}
        >
          SECTOR
        </p>
      </div>
      <div className={`${isExpanded ? "d-block" : "d-none"}`}>
        {sourceTypes.includes("company") && (
          <MultiSelectSectorWithSelectAll
            classificationOptionsList={filterOptions.industry_classifications}
            filterKey="industry_classifications"
            title="Industry Sector"
            allClassificationsType="company"
            currentFilterValue={industry_classifications}
            all_classifications={all_classifications}
            updateFilter={updateFilter}
            updateAllClassificationsToggle={updateAllClassificationsToggle}
          />
        )}
        {sourceTypes.includes("private_company") && (
          <MultiSelectSectorWithSelectAll
            classificationOptionsList={filterOptions.private_classifications}
            filterKey="private_classifications"
            title="Private Sector"
            allClassificationsType="private"
            currentFilterValue={private_classifications}
            all_classifications={all_classifications}
            updateFilter={updateFilter}
            updateAllClassificationsToggle={updateAllClassificationsToggle}
          />
        )}
        {sourceTypes.includes("superannuation") && (
          <MultiSelectSectorWithSelectAll
            classificationOptionsList={
              filterOptions.superannuation_classifications
            }
            filterKey="superannuation_classifications"
            title="Superannuation Sector"
            allClassificationsType="superannuation"
            currentFilterValue={superannuation_classifications}
            all_classifications={all_classifications}
            updateFilter={updateFilter}
            updateAllClassificationsToggle={updateAllClassificationsToggle}
          />
        )}
        {sourceTypes.includes("government") && (
          <MultiSelectSectorWithSelectAll
            classificationOptionsList={filterOptions.government_classifications}
            filterKey="government_classifications"
            title="Major Govt. Sector"
            allClassificationsType="government"
            currentFilterValue={government_classifications}
            all_classifications={all_classifications}
            updateFilter={updateFilter}
            updateAllClassificationsToggle={updateAllClassificationsToggle}
          />
        )}
        {sourceTypes.includes("other_government") && (
          <MultiSelectSectorWithSelectAll
            classificationOptionsList={
              filterOptions.other_government_classifications
            }
            filterKey="other_government_classifications"
            title="Minor Govt. Sector"
            allClassificationsType="other_government"
            currentFilterValue={other_government_classifications}
            all_classifications={all_classifications}
            updateFilter={updateFilter}
            updateAllClassificationsToggle={updateAllClassificationsToggle}
          />
        )}
        {sourceTypes.includes("nfp") && (
          <MultiSelectSectorWithSelectAll
            classificationOptionsList={filterOptions.nfp_classifications}
            filterKey="nfp_classifications"
            title="Not For Profit Sector"
            allClassificationsType="nfp"
            currentFilterValue={nfp_classifications}
            all_classifications={all_classifications}
            updateFilter={updateFilter}
            updateAllClassificationsToggle={updateAllClassificationsToggle}
          />
        )}
      </div>
    </div>
  );
};
export default SectorFilter;
